// Client-side helpers for the Convene UI (Home / Login / pre-join).

export interface RecentMeeting {
  code: string;
  name: string;
  at: number; // unix ms
}

const RECENTS_KEY = 'convene:recents';
const RECENTS_MAX = 6;

// Accepts a raw code ("abc-defg-hij"), a full link
// ("https://host/rooms/abc-defg-hij"), or a legacy 2-group id ("abcd-1234").
export function extractRoomCode(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;

  // If it's a URL, take the last path segment.
  let candidate = raw;
  try {
    if (/^https?:\/\//i.test(raw)) {
      const segs = new URL(raw).pathname.split('/').filter(Boolean);
      candidate = segs[segs.length - 1] ?? '';
    }
  } catch {
    /* not a URL — fall through */
  }

  candidate = candidate.toLowerCase();
  // Meet-style code, or the legacy generateRoomId() form. Keep it permissive
  // so any server-generated room name works.
  if (/^[a-z0-9]+(-[a-z0-9]+)*$/.test(candidate)) return candidate;
  return null;
}

// Ask the server whether a room with this code exists. Returns 'not-found' for a
// definitive 404 so the caller can tell the user; 'error' (network/5xx) is left
// to the caller to fail open, since a transient hiccup shouldn't block a valid
// join.
export async function checkRoom(code: string): Promise<'exists' | 'not-found' | 'error'> {
  try {
    const res = await fetch(`/api/meetings/${encodeURIComponent(code)}`, { cache: 'no-store' });
    if (res.ok) return 'exists';
    if (res.status === 404) return 'not-found';
    return 'error';
  } catch {
    return 'error';
  }
}

export function loadRecents(): RecentMeeting[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENTS_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as RecentMeeting[]) : [];
  } catch {
    return [];
  }
}

export function rememberRecent(code: string, name = ''): void {
  if (typeof window === 'undefined') return;
  const existing = loadRecents().filter((r) => r.code !== code);
  const next = [{ code, name, at: Date.now() }, ...existing].slice(0, RECENTS_MAX);
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* storage full / disabled — ignore */
  }
}
